import { initializeApp } from 'firebase/app';
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// 1. Liste des variables obligatoires
const requiredVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
];

// 2. On vérifie au démarrage que tout est défini
requiredVars.forEach((key) => {
  if (!import.meta.env[key]) {
    console.error(`❌ Variable d'environnement manquante : ${key}`);
  }
});

// 3. Configuration Firebase construite à partir des variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// 4. On initialise Firebase et on exporte les services utilisés
export const app = initializeApp(firebaseConfig);

// 5. App Check (anti-bot) — actif si VITE_APP_CHECK_RECAPTCHA_KEY est défini.
// Permet le déploiement progressif : on initialise côté client d'abord, puis on
// active `enforceAppCheck: true` côté Cloud Functions une fois validé.
// En dev, on peut définir VITE_APP_CHECK_DEBUG_TOKEN pour utiliser un token debug.
const recaptchaKey = import.meta.env.VITE_APP_CHECK_RECAPTCHA_KEY as string | undefined;
if (recaptchaKey) {
  if (import.meta.env.DEV && import.meta.env.VITE_APP_CHECK_DEBUG_TOKEN) {
    // Token debug Firebase App Check (uniquement en dev local)
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      import.meta.env.VITE_APP_CHECK_DEBUG_TOKEN as string;
  }
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(recaptchaKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    console.error('[AppCheck] init error:', err);
  }
}

// Persistance configurée SYNCHRONIQUEMENT à la création (et non dans un effet
// React async). Repli multi-niveaux : IndexedDB → localStorage → sessionStorage.
// La session est relue depuis le premier stockage disponible — robuste aux
// cold-starts iOS (PWA standalone) où la lecture IndexedDB peut renvoyer vide
// juste après un rechargement (ex. au retour d'un changement de réseau).
// initializeAuth doit être appelé une seule fois, avant tout getAuth(app).
export const auth = initializeAuth(app, {
  persistence: [
    indexedDBLocalPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
  ],
});
export const db = getFirestore(app);
export const storage = getStorage(app);
// europe-west1 : doit matcher la région des Cloud Functions marketplace
// (cf. SECURITY_OPS.md §3). Toutes les CFs sont déployées en europe-west1.
export const functions = getFunctions(app, 'europe-west1');
